import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { ChannelCard } from "pages/liveTvPage/shared/ChannelCard";
import MovieCarouselCard from "pages/videoPage/shared/MovieCarouselCard";
import { cards } from "./constant/constant";
import PackageDetailsHeroCard from "./shared/PackageDetailsHeroCard";
import PackageDetailsCtaCard from "./shared/PackageDetailsCtaCard";

import MusicCard from "pages/musicPage/shared/MusicCard";
import Podcastcard from "pages/podcastPage/shared/Podcastcard";
import RadioCard from "pages/radioPage/shared/RadioCard";
import SubscriptionService from "services/subscription/subscriptionService";
import NewPodcastSkeleton from "components/skeleton/NewPodcastSkeleton";
import RadioSkeleton from "components/skeleton/RadioSkeleton";
import TVChanneSkeleton from "components/skeleton/TVChanneSkeleton";
import TrendMovieSkeletion from "components/skeleton/TrendMovieSkeletion";
import {
  MiniPackagesResponse,
  PackageDetails,
} from "services/subscription/types";
import AlbumCard from "pages/musicPage/shared/AlbumCard";
import { useSelector } from "react-redux";

// consistent modern section heading
const SectionHeading = ({ text }: { text: string }) => (
  <div className="mb-5 flex items-center gap-3">
    <span className="h-6 w-1.5 rounded-full bg-[var(--red)]" />
    <p className="text-[22px] font-bold tracking-tight text-white max-sm:text-[18px]">
      {text}
    </p>
  </div>
);

// shared grid class for all content rows
const gridClass =
  "grid w-full grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 2xl:grid-cols-7 max-sm:gap-3";

const FE_URL = process.env.REACT_APP_FRONTEND_URL;

const PackageDetailsPage = () => {
  const loggedInUser = useSelector((state: any) => state?.user?.currentUser);
  const location = useLocation();
  const selectedCard: MiniPackagesResponse =
    location?.state?.card || cards.find((card) => card.id === 5) || cards[0];
  const [packageDetails, setPackageDetails] = useState<PackageDetails>();
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const seoTitle = `${packageDetails?.title || selectedCard?.title} - AYOZAT Subscription`;
  const seoDescription =
    packageDetails?.description ||
    selectedCard?.description ||
    "Explore this AYOZAT mini package and unlock its channels, movies, music and more.";
  const seoImage =
    packageDetails?.thumbnail ||
    selectedCard?.thumbnail ||
    selectedCard?.banner;
  const seoUrl = `${FE_URL}/subscription/plan/${selectedCard?.slug}`;

  //fetch packages details
  const fetchMiniPackagesDetailsById = async () => {
    try {
      setIsLoading(true);
      const resp = await SubscriptionService.getMiniPackagesDetailsBySlug(
        selectedCard?.slug,
      );
      if (resp?.status) {
        setPackageDetails(resp.data);
        console.log("packageDetails :::", packageDetails?.is_subscribed);
      }
    } catch (err) {
      console.log("err :::", err);
    } finally {
      setIsLoading(false);
    }
  };

  //get all paid content
  useEffect(() => {
    fetchMiniPackagesDetailsById();
  }, [selectedCard?.slug]);

  // scroll to top on load
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, []);

  return (
    <>
      <Helmet>
        <title>{seoTitle}</title>
        <meta name="description" content={seoDescription} />
        <link rel="canonical" href={seoUrl} />
        <meta property="og:title" content={seoTitle} />
        <meta property="og:description" content={seoDescription} />
        <meta property="og:url" content={seoUrl} />
        <meta property="og:type" content="website" />
        {seoImage && <meta property="og:image" content={seoImage} />}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={seoTitle} />
        <meta name="twitter:description" content={seoDescription} />
        {seoImage && <meta name="twitter:image" content={seoImage} />}
      </Helmet>
      <div className="pb-16 text-white">
      <div className="w-full pt-6">
        <PackageDetailsHeroCard
          card={packageDetails ? { ...selectedCard, ...packageDetails } : selectedCard}
          isSubscription={packageDetails?.is_subscribed ?? false}
          isLoading={isLoading}
        />

        {/* live tv channel section */}
        {isLoading ? (
          <TVChanneSkeleton />
        ) : (
          packageDetails?.contents?.tv_channel &&
          packageDetails?.contents?.tv_channel?.length > 0 && (
            <section className="mt-12">
              <SectionHeading text="TV Channels for You" />
              <div className={gridClass}>
                {packageDetails?.contents?.tv_channel.map((channel: any) => (
                  <ChannelCard
                    key={channel.id}
                    data={channel}
                    img={channel.thumbnail}
                  />
                ))}
              </div>
            </section>
          )
        )}

        {/* movies section */}
        {isLoading ? (
          <TrendMovieSkeletion />
        ) : (
          packageDetails?.contents?.movie &&
          packageDetails?.contents?.movie?.length > 0 && (
            <section className="mt-12">
              <SectionHeading text="Unlock Premium Movies" />
              <div className={gridClass}>
                {packageDetails?.contents?.movie.map((card: any) => (
                  <MovieCarouselCard
                    key={card?.id}
                    id={card?.id}
                    time={card?.runtime}
                    title={card?.title}
                    info={card?.title}
                    year={card?.released_at}
                    type={card?.genre}
                    img={card?.thumbnail}
                    thumbnail_url={card?.thumbnail_url}
                    slug={card?.slug}
                    movie={card}
                    plan_id={card?.plan_id}
                  />
                ))}
              </div>
            </section>
          )
        )}

        {/* series section */}
        {isLoading ? (
          <TrendMovieSkeletion />
        ) : (
          packageDetails?.contents?.tv_show &&
          packageDetails?.contents?.tv_show?.length > 0 && (
            <section className="mt-12">
              <SectionHeading text="Exclusive Shows" />
              <div className={gridClass}>
                {packageDetails?.contents?.tv_show.map((card: any) => (
                  <MovieCarouselCard
                    key={card?.id}
                    id={card?.id}
                    title={card?.title}
                    img={card?.thumbnail}
                    seasons={card?.no_of_seasons}
                    slug={card?.slug}
                    tv={true}
                    tvSeries={card}
                    first_episode={card?.first_episode}
                    plan_id={card?.plan_id}
                  />
                ))}
              </div>
            </section>
          )
        )}

        {/* music section */}
        {isLoading ? (
          <NewPodcastSkeleton />
        ) : (
          packageDetails?.contents?.song &&
          packageDetails?.contents?.song?.length > 0 && (
            <section className="mt-12">
              <SectionHeading text="Unlock the Music" />
              <div className={gridClass}>
                {packageDetails?.contents?.song.map((song: any) => (
                  <MusicCard key={song?.id} song={song} isLoading={isLoading} />
                ))}
              </div>
            </section>
          )
        )}

        {/* album section */}
        {isLoading ? (
          <NewPodcastSkeleton />
        ) : (
          packageDetails?.contents?.album &&
          packageDetails?.contents?.album?.length > 0 && (
            <section className="mt-12">
              <SectionHeading text="Unlock Featured Albums" />
              <div className={gridClass}>
                {packageDetails?.contents?.album.map((album: any) => (
                  <AlbumCard key={album?.id} {...album} album={album} />
                ))}
              </div>
            </section>
          )
        )}

        {/* podcast section */}
        {isLoading ? (
          <NewPodcastSkeleton />
        ) : (
          packageDetails?.contents?.podcast &&
          packageDetails?.contents?.podcast?.length > 0 && (
            <section className="mt-12">
              <SectionHeading text="Exclusive Podcasts" />
              <div className={gridClass}>
                {packageDetails?.contents?.podcast?.map((podcast: any) => (
                  <Podcastcard key={podcast?.id} podcast={podcast} />
                ))}
              </div>
            </section>
          )
        )}

        {/* radio section */}
        {isLoading ? (
          <RadioSkeleton />
        ) : (
          packageDetails?.contents?.radio_station &&
          packageDetails?.contents?.radio_station?.length > 0 && (
            <section className="mt-12">
              <SectionHeading text="Unlock Live Radio Stations" />
              <div className={gridClass}>
                {packageDetails?.contents?.radio_station.map((station: any) => (
                  <RadioCard key={station?.id} station={station} />
                ))}
              </div>
            </section>
          )
        )}

        {loggedInUser?.role !== 'plus_subscription' && (
          <PackageDetailsCtaCard selectedPackage={selectedCard} />
        )}
      </div>
      </div>
    </>
  );
};

export default PackageDetailsPage;